import React, { useState, useEffect } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { collection, getDocs, updateDoc, doc, addDoc, query, orderBy, where, serverTimestamp } from 'firebase/firestore';
import { BrandGradient } from '../components/common/PFILogo';
import { 
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PaperAirplaneIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

export default function QueryManagementPage() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [response, setResponse] = useState('');
  const [isResponding, setIsResponding] = useState(false);

  useEffect(() => {
    loadQueries();
  }, []);

  const loadQueries = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'supplierQueries'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const queriesData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          responseAt: data.responseAt?.toDate()
        };
      });
      
      setQueries(queriesData);
    } catch (error) {
      console.error('Error loading queries:', error);
      alert('Failed to load queries: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (queryId) => {
    if (!response.trim()) {
      alert('Please enter a response');
      return;
    }

    setIsResponding(true);
    try {
      const now = new Date();
      
      // Update query with response
      await updateDoc(doc(db, 'supplierQueries', queryId), {
        response: response,
        responseBy: 'Pink Flamingo Interiors',
        responseAt: now,
        status: 'responded',
        updatedAt: now
      });

      const query = queries.find(q => q.id === queryId);
      
      // Update task status
      await updateDoc(doc(db, 'projects', query.projectId, 'tasks', query.taskId), {
        status: 'queries_resolved',
        updatedAt: now
      });

      // Notify supplier
      await addDoc(collection(db, 'notifications'), {
        type: 'query-response',
        recipientId: query.supplierId,
        recipientEmail: query.supplierEmail || '',
        recipientName: query.supplierName,
        projectId: query.projectId,
        taskId: query.taskId,
        queryId: queryId,
        originalQuery: query.queryText,
        responseText: response,
        message: `Response to your query: ${query.queryText.substring(0, 100)}...`,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      alert('✅ Response sent successfully!');
      setSelectedQuery(null);
      setResponse('');
      loadQueries();
      
    } catch (error) {
      console.error('Error responding to query:', error);
      alert('Failed to send response: ' + error.message);
    } finally {
      setIsResponding(false);
    }
  };

  const handleMarkResolved = async (queryId) => {
    try {
      await updateDoc(doc(db, 'supplierQueries', queryId), {
        status: 'resolved',
        updatedAt: new Date()
      });
      
      loadQueries();
    } catch (error) {
      console.error('Error marking query as resolved:', error);
      alert('Failed to update query: ' + error.message);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'responded': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading queries...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-start">
            <ChatBubbleLeftRightIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">Query Management</h1>
              <p className="text-pink-100 text-xl">Supplier questions and clarifications</p>
              <div className="flex items-center space-x-6 text-pink-100 mt-4">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                  {queries.filter(q => q.status === 'pending').length} Pending
                </div>
                <div className="flex items-center">
                  <ClockIcon className="w-5 h-5 mr-2" />
                  {queries.filter(q => q.status === 'responded').length} Responded
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  {queries.filter(q => q.status === 'resolved').length} Resolved
                </div>
              </div>
            </div>
          </div>
        </BrandGradient>

        {/* Queries List */}
        <div className="space-y-6">
          {queries.map((query) => (
            <div key={query.id} className="bg-white rounded-2xl shadow-xl border border-pink-100 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(query.priority)}`}>
                      {query.priority.toUpperCase()}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(query.status)}`}>
                      {query.status.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {query.category}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Query from {query.supplierName}
                  </h3>
                  
                  <div className="text-sm text-gray-600 mb-3">
                    <p><strong>Project:</strong> {query.projectId}</p>
                    <p><strong>Task:</strong> {query.taskId}</p>
                    <p><strong>Submitted:</strong> {query.createdAt.toLocaleDateString()} at {query.createdAt.toLocaleTimeString()}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedQuery(selectedQuery?.id === query.id ? null : query)}
                    className="flex items-center px-4 py-2 bg-blue-50 text-blue-600 text-sm font-medium rounded hover:bg-blue-100 transition-colors"
                  >
                    <EyeIcon className="w-4 h-4 mr-2" />
                    {selectedQuery?.id === query.id ? 'Hide' : 'View'}
                  </button>
                  
                  {query.status === 'responded' && (
                    <button
                      onClick={() => handleMarkResolved(query.id)}
                      className="flex items-center px-4 py-2 bg-green-50 text-green-600 text-sm font-medium rounded hover:bg-green-100 transition-colors"
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                      Mark Resolved
                    </button>
                  )}
                </div>
              </div>

              {/* Query Details */}
              {selectedQuery?.id === query.id && (
                <div className="border-t pt-4">
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Query Details</h4>
                    <p className="text-gray-700">{query.queryText}</p>
                  </div>

                  {query.response && (
                    <div className="bg-blue-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-blue-900 mb-2">
                        Response by {query.responseBy}
                        <span className="text-sm font-normal text-blue-700 ml-2">
                          on {query.responseAt?.toLocaleDateString()}
                        </span>
                      </h4>
                      <p className="text-blue-800">{query.response}</p>
                    </div>
                  )}

                  {query.status === 'pending' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Response to Supplier
                        </label>
                        <textarea
                          value={response}
                          onChange={(e) => setResponse(e.target.value)}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          placeholder="Enter your response to the supplier's query..."
                        />
                      </div>
                      
                      <button
                        onClick={() => handleRespond(query.id)}
                        disabled={!response.trim() || isResponding}
                        className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                          response.trim() && !isResponding
                            ? 'bg-pink-600 text-white hover:bg-pink-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {isResponding ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                            Send Response
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {queries.length === 0 && (
            <div className="text-center py-12">
              <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No queries yet</h3>
              <p className="text-gray-500">Supplier queries will appear here when submitted</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}